import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowRight, Bot, CircleAlert, Clock3, Sparkles } from "lucide-react";
import ApplicantCoachAvatar from "./ApplicantCoachAvatar";
import { useApplicantCoach } from "./ApplicantCoachContext";
import { getApplicantBriefingDayKey } from "./applicantDailyBriefingModel";

const COPY = {
  de: {
    coachLabel: "KI/AI Coach",
    summary: {
      urgent: "Heute solltest du zuerst auf diese drei Punkte schauen.",
      attention: "Heute sind diese drei Punkte für dich wichtig.",
      opportunity: "Du bist auf gutem Kurs. Diese drei Punkte bringen heute den meisten Nutzwert.",
      steady: "Dein Stand wirkt ruhig. Diese drei Punkte lohnen heute den ersten Blick.",
    },
    loadingTitle: "Tägliches Briefing wird vorbereitet",
    loadingText: "Ich ziehe gerade Messaging Board, sichtbare Blocker und die nächste Chance zusammen.",
    errorTitle: "Briefing gerade nicht vollständig",
    errorText: "Der Applicant-Stand wird gerade nachgeladen. Öffne bei Bedarf direkt dein Postfach oder den nächsten Bereich.",
    emptyTitle: "Heute keine dominanten offenen Punkte",
    emptyText: "Gerade ist kein harter offener Punkt obenauf. Prüfe kurz dein Postfach und halte dein Profil aktuell.",
    labels: {
      what: "Was ist los",
      why: "Warum wichtig",
      next: "Nächster Schritt",
      compact: "Tagesbriefing",
      obligation: "Pflicht & Frist",
      blocker: "Sichtbarkeit & Blocker",
      accelerator: "Chance & Beschleuniger",
    },
    actions: {
      now: "Jetzt ansehen",
      explain: "Erklären",
      later: "Später",
      hideToday: "Heute nicht mehr zeigen",
      openCase: "Zum Fall",
      openMessage: "Zur Nachricht",
      openArea: "Zum Bereich",
      openOpportunity: "Zur Chance",
      openBoard: "Postfach öffnen",
      respondCase: "Rückmeldung senden",
      completeProfile: "Profil ergänzen",
      completeCv: "CV ergänzen",
      completeSkills: "Skills ergänzen",
      startLanguage: "Sprachtest starten",
      continueLanguage: "Sprachtest fortsetzen",
      reviewRelease: "Freigabe prüfen",
      openMatches: "Chancen öffnen",
      reviewProgress: "Fortschritt prüfen",
      reviewAccount: "Konto prüfen",
      nextGeneric: "Nächsten Schritt öffnen",
    },
    greetings: {
      morning: "Guten Morgen",
      day: "Guten Tag",
      evening: "Guten Abend",
    },
  },
  en: {
    coachLabel: "AI Coach",
    summary: {
      urgent: "These are the three points to review first today.",
      attention: "These are the three most important points for today.",
      opportunity: "You are on a good track. These three points add the most value today.",
      steady: "Your status looks calm. These three points are still worth a first look today.",
    },
    loadingTitle: "Preparing your daily briefing",
    loadingText: "I am pulling together your messaging board, visible blockers, and next opportunity.",
    errorTitle: "Briefing not fully available right now",
    errorText: "Your applicant status is still loading. You can open your inbox or continue with the next area.",
    emptyTitle: "No dominant open item today",
    emptyText: "There is no hard open item at the top right now. Check your inbox briefly and keep your profile current.",
    labels: {
      what: "What is happening",
      why: "Why it matters",
      next: "Next step",
      compact: "Daily briefing",
      obligation: "Deadlines & duties",
      blocker: "Visibility & blockers",
      accelerator: "Opportunities & momentum",
    },
    actions: {
      now: "View now",
      explain: "Explain",
      later: "Later",
      hideToday: "Hide for today",
      openCase: "Open case",
      openMessage: "Open message",
      openArea: "Open area",
      openOpportunity: "Open opportunity",
      openBoard: "Open inbox",
      respondCase: "Send response",
      completeProfile: "Complete profile",
      completeCv: "Complete CV",
      completeSkills: "Add skills",
      startLanguage: "Start language test",
      continueLanguage: "Continue language test",
      reviewRelease: "Review release",
      openMatches: "Open opportunities",
      reviewProgress: "Review progress",
      reviewAccount: "Review account",
      nextGeneric: "Open next step",
    },
    greetings: {
      morning: "Good morning",
      day: "Good day",
      evening: "Good evening",
    },
  },
  fr: {
    coachLabel: "AI Coach",
    summary: {
      urgent: "Aujourd'hui, regarde d'abord ces trois points.",
      attention: "Aujourd'hui, ces trois points sont les plus importants.",
      opportunity: "Tu es sur une bonne trajectoire. Ces trois points apportent le plus aujourd'hui.",
      steady: "Ta situation est calme. Ces trois points valent quand meme un premier regard.",
    },
    loadingTitle: "Preparation de ton briefing du jour",
    loadingText: "Je rassemble ton tableau de messages, les blocages visibles et la prochaine opportunite.",
    errorTitle: "Briefing incomplet pour le moment",
    errorText: "Ton statut applicant se recharge encore. Tu peux ouvrir ta messagerie ou continuer dans la zone suivante.",
    emptyTitle: "Aucun point dominant aujourd'hui",
    emptyText: "Aucun point prioritaire n'est en tete pour le moment. Verifie brievement ta messagerie et garde ton profil a jour.",
    labels: {
      what: "Ce qui se passe",
      why: "Pourquoi c'est important",
      next: "Prochaine etape",
      compact: "Briefing du jour",
      obligation: "Obligations & echeances",
      blocker: "Visibilite & blocages",
      accelerator: "Opportunites & elan",
    },
    actions: {
      now: "Voir maintenant",
      explain: "Expliquer",
      later: "Plus tard",
      hideToday: "Ne plus montrer aujourd'hui",
      openCase: "Ouvrir le dossier",
      openMessage: "Ouvrir le message",
      openArea: "Ouvrir la zone",
      openOpportunity: "Voir l'opportunite",
      openBoard: "Ouvrir la messagerie",
      respondCase: "Repondre",
      completeProfile: "Completer le profil",
      completeCv: "Completer le CV",
      completeSkills: "Ajouter des competences",
      startLanguage: "Demarrer le test",
      continueLanguage: "Continuer le test",
      reviewRelease: "Verifier la diffusion",
      openMatches: "Ouvrir les opportunites",
      reviewProgress: "Voir la progression",
      reviewAccount: "Verifier le compte",
      nextGeneric: "Ouvrir l'etape suivante",
    },
    greetings: {
      morning: "Bonjour",
      day: "Bonjour",
      evening: "Bonsoir",
    },
  },
  tr: {
    coachLabel: "AI Coach",
    summary: {
      urgent: "Bugun once bu uc noktaya bak.",
      attention: "Bugun en onemli uc nokta bunlar.",
      opportunity: "Iyi bir yoldasin. Bugun en cok degeri bu uc nokta saglar.",
      steady: "Durumun sakin gorunuyor. Yine de once bu uc noktaya bakmaya deger.",
    },
    loadingTitle: "Gunluk brifingin hazirlaniyor",
    loadingText: "Mesaj panonu, gorunur engelleri ve bir sonraki firsati bir araya getiriyorum.",
    errorTitle: "Brifing su anda tam degil",
    errorText: "Applicant durumun hala yukleniyor. Gelen kutunu acabilir veya sonraki alana gecebilirsin.",
    emptyTitle: "Bugun baskin acik nokta yok",
    emptyText: "Su anda en ustte sert bir acik nokta yok. Gelen kutunu kisaca kontrol et ve profilini guncel tut.",
    labels: {
      what: "Ne oluyor",
      why: "Neden onemli",
      next: "Sonraki adim",
      compact: "Gunluk brifing",
      obligation: "Gorevler & son tarihler",
      blocker: "Gorunurluk & engeller",
      accelerator: "Firsatlar & hiz",
    },
    actions: {
      now: "Simdi gor",
      explain: "Acikla",
      later: "Daha sonra",
      hideToday: "Bugun bir daha gosterme",
      openCase: "Dosyayi ac",
      openMessage: "Mesaji ac",
      openArea: "Alani ac",
      openOpportunity: "Firsati ac",
      openBoard: "Gelen kutusunu ac",
      respondCase: "Yanit gonder",
      completeProfile: "Profili tamamla",
      completeCv: "CV'yi tamamla",
      completeSkills: "Beceri ekle",
      startLanguage: "Dil testini baslat",
      continueLanguage: "Dil testine devam et",
      reviewRelease: "Paylasimi kontrol et",
      openMatches: "Firsatlari ac",
      reviewProgress: "Ilerlemeyi kontrol et",
      reviewAccount: "Hesabi kontrol et",
      nextGeneric: "Sonraki adimi ac",
    },
    greetings: {
      morning: "Gunaydin",
      day: "Iyi gunler",
      evening: "Iyi aksamlar",
    },
  },
  vi: {
    coachLabel: "AI Coach",
    summary: {
      urgent: "Hom nay hay xem truoc ba diem nay.",
      attention: "Hom nay day la ba diem quan trong nhat.",
      opportunity: "Ban dang di dung huong. Ba diem nay tao gia tri nhieu nhat hom nay.",
      steady: "Trang thai cua ban kha on dinh. Ba diem nay van dang de xem truoc.",
    },
    loadingTitle: "Dang chuan bi ban briefing hang ngay",
    loadingText: "Toi dang tong hop bang tin nhan, cac diem chan hien thi va co hoi tiep theo cua ban.",
    errorTitle: "Ban briefing hien chua day du",
    errorText: "Trang thai applicant cua ban van dang tai. Ban co the mo hop thu hoac tiep tuc khu vuc tiep theo.",
    emptyTitle: "Hom nay khong co diem mo noi bat",
    emptyText: "Hien tai khong co diem mo bat buoc nao o tren cung. Hay kiem tra nhanh hop thu va giu ho so luon cap nhat.",
    labels: {
      what: "Dang co gi",
      why: "Vì sao quan trọng",
      next: "Buoc tiep theo",
      compact: "Briefing hang ngay",
      obligation: "Han & nghia vu",
      blocker: "Do hien thi & diem chan",
      accelerator: "Co hoi & dong luc",
    },
    actions: {
      now: "Xem ngay",
      explain: "Giai thich",
      later: "De sau",
      hideToday: "Hom nay dung hien nua",
      openCase: "Mo ho so",
      openMessage: "Mo tin nhan",
      openArea: "Mo khu vuc",
      openOpportunity: "Mo co hoi",
      openBoard: "Mo hop thu",
      respondCase: "Gui phan hoi",
      completeProfile: "Bo sung ho so",
      completeCv: "Bo sung CV",
      completeSkills: "Them ky nang",
      startLanguage: "Bat dau bai test",
      continueLanguage: "Tiep tuc bai test",
      reviewRelease: "Kiem tra chia se",
      openMatches: "Mo co hoi",
      reviewProgress: "Xem tien do",
      reviewAccount: "Kiem tra tai khoan",
      nextGeneric: "Mo buoc tiep theo",
    },
    greetings: {
      morning: "Chao buoi sang",
      day: "Chao ban",
      evening: "Chao buoi toi",
    },
  },
  uk: {
    coachLabel: "AI Coach",
    summary: {
      urgent: "Sohodni spershu perehlyan tsі try punkty.",
      attention: "Sohodni tse try naivazhlyvishi punkty.",
      opportunity: "Ty na khoroshomu kursi. Tsі try punkty dayut naybilshu koryst sohodni.",
      steady: "Tviy status vyhlyadaye spokiyno. Vse odno tsі try punkty varti pershoho pohlyadu.",
    },
    loadingTitle: "Hotuemo tviy shchodennyy bryfing",
    loadingText: "Ya zbyrayu tvoyu doshku povidomlen, vydymi blokery ta nastupnu mozhlyvist.",
    errorTitle: "Bryfing zaraz ne povnistyu dostupnyy",
    errorText: "Tviy applicant-status shche zavantazhuetsya. Ty mozhesh vidkryty inbox abo prodovzhyty v nastupniy zoni.",
    emptyTitle: "Sohodni nemae dominantnoho vidkrytoho punktu",
    emptyText: "Zaraz nahori nemae zhorskoyi vidkrytoyi spravy. Korotko perevir inbox i trymay profil aktualnym.",
    labels: {
      what: "Shcho vidbuvayetsya",
      why: "Chomu tse vazhlyvo",
      next: "Nastupnyy krok",
      compact: "Shchodennyy bryfing",
      obligation: "Obovyazky & stroky",
      blocker: "Vydymist & blokery",
      accelerator: "Mozhlyvosti & impuls",
    },
    actions: {
      now: "Perehlyanuty zaraz",
      explain: "Poyasnyty",
      later: "Piznishe",
      hideToday: "Sohodni bilshe ne pokazuvaty",
      openCase: "Vidkryty spravu",
      openMessage: "Vidkryty povidomlennya",
      openArea: "Vidkryty rozdil",
      openOpportunity: "Vidkryty mozhlyvist",
      openBoard: "Vidkryty inbox",
      respondCase: "Nadislati vidpovid",
      completeProfile: "Dopovnyty profil",
      completeCv: "Dopovnyty CV",
      completeSkills: "Dodaty navychky",
      startLanguage: "Pochaty movnyy test",
      continueLanguage: "Prodovzhyty movnyy test",
      reviewRelease: "Pereviryty publikatsiyu",
      openMatches: "Vidkryty mozhlyvosti",
      reviewProgress: "Perehlyanuty progres",
      reviewAccount: "Pereviryty akaunt",
      nextGeneric: "Vidkryty nastupnyy krok",
    },
    greetings: {
      morning: "Dobroho ranku",
      day: "Dobryy den",
      evening: "Dobryy vechir",
    },
  },
  bks: {
    coachLabel: "AI Coach",
    summary: {
      urgent: "Danas prvo pogledaj ove tri tacke.",
      attention: "Danas su ove tri tacke najvaznije.",
      opportunity: "Na dobrom si putu. Ove tri tacke danas donose najvise koristi.",
      steady: "Tvoj status djeluje mirno. Ipak, ove tri tacke vrijedi prvo pogledati.",
    },
    loadingTitle: "Pripremam tvoj dnevni briefing",
    loadingText: "Spajam tvoju tablu poruka, vidljive blokere i sljedecu priliku.",
    errorTitle: "Briefing trenutno nije potpun",
    errorText: "Tvoj applicant status se jos ucitava. Po potrebi otvori inbox ili predji u sljedeci dio.",
    emptyTitle: "Danas nema dominantne otvorene tacke",
    emptyText: "Trenutno na vrhu nema tvrdog otvorenog punkta. Kratko provjeri inbox i drzi profil azurnim.",
    labels: {
      what: "Sta se desava",
      why: "Zasto je vazno",
      next: "Sljedeci korak",
      compact: "Dnevni briefing",
      obligation: "Obaveze & rokovi",
      blocker: "Vidljivost & blokeri",
      accelerator: "Prilike & ubrzanje",
    },
    actions: {
      now: "Pogledaj sada",
      explain: "Objasni",
      later: "Kasnije",
      hideToday: "Ne prikazuj vise danas",
      openCase: "Otvori slucaj",
      openMessage: "Otvori poruku",
      openArea: "Otvori dio",
      openOpportunity: "Otvori priliku",
      openBoard: "Otvori inbox",
      respondCase: "Posalji odgovor",
      completeProfile: "Dopuni profil",
      completeCv: "Dopuni CV",
      completeSkills: "Dodaj vjestine",
      startLanguage: "Pokreni jezicki test",
      continueLanguage: "Nastavi jezicki test",
      reviewRelease: "Provjeri objavu",
      openMatches: "Otvori prilike",
      reviewProgress: "Provjeri napredak",
      reviewAccount: "Provjeri nalog",
      nextGeneric: "Otvori sljedeci korak",
    },
    greetings: {
      morning: "Dobro jutro",
      day: "Dobar dan",
      evening: "Dobro vece",
    },
  },
  ar: {
    coachLabel: "AI Coach",
    summary: {
      urgent: "اليوم ابدأ بهذه النقاط الثلاث.",
      attention: "اليوم هذه هي النقاط الثلاث الأهم.",
      opportunity: "أنت على مسار جيد. هذه النقاط الثلاث تعطي أكبر فائدة اليوم.",
      steady: "وضعك يبدو هادئاً. ومع ذلك تستحق هذه النقاط الثلاث نظرة أولى اليوم.",
    },
    loadingTitle: "يجري إعداد موجزك اليومي",
    loadingText: "أجمع الآن لوحة الرسائل والعوائق الظاهرة والفرصة التالية.",
    errorTitle: "الموجز غير مكتمل حالياً",
    errorText: "حالة Applicant الخاصة بك ما زالت تُحمَّل. يمكنك فتح صندوق الرسائل أو متابعة القسم التالي.",
    emptyTitle: "لا توجد نقطة مفتوحة مهيمنة اليوم",
    emptyText: "لا توجد حالياً نقطة إلزامية حادة في الأعلى. افحص صندوق الرسائل سريعاً وابق ملفك محدثاً.",
    labels: {
      what: "ما الذي يحدث",
      why: "لماذا هذا مهم",
      next: "الخطوة التالية",
      compact: "الموجز اليومي",
      obligation: "المهام والمواعيد",
      blocker: "الظهور والعوائق",
      accelerator: "الفرص والتسريع",
    },
    actions: {
      now: "اعرض الآن",
      explain: "اشرح",
      later: "لاحقاً",
      hideToday: "لا تعرضه اليوم مرة أخرى",
      openCase: "افتح الحالة",
      openMessage: "افتح الرسالة",
      openArea: "افتح القسم",
      openOpportunity: "افتح الفرصة",
      openBoard: "افتح صندوق الرسائل",
      respondCase: "أرسل الرد",
      completeProfile: "أكمل الملف",
      completeCv: "أكمل السيرة",
      completeSkills: "أضف المهارات",
      startLanguage: "ابدأ اختبار اللغة",
      continueLanguage: "تابع اختبار اللغة",
      reviewRelease: "راجع النشر",
      openMatches: "افتح الفرص",
      reviewProgress: "راجع التقدم",
      reviewAccount: "راجع الحساب",
      nextGeneric: "افتح الخطوة التالية",
    },
    greetings: {
      morning: "صباح الخير",
      day: "مرحباً",
      evening: "مساء الخير",
    },
  },
};

const SUPPORTED_BRIEFING_LANGUAGES = new Set(Object.keys(COPY));

const LOCALES = {
  ar: "ar-EG",
  bks: "bs-BA",
  de: "de-DE",
  en: "en-US",
  fr: "fr-FR",
  tr: "tr-TR",
  uk: "uk-UA",
  vi: "vi-VN",
};

const LANE_ICONS = {
  obligation: Clock3,
  blocker: CircleAlert,
  accelerator: Sparkles,
};

function getCopy(languageCode) {
  return COPY[languageCode] || COPY.en;
}

function getBriefingLanguageCode(replyLanguage) {
  const code = String(replyLanguage?.code || "en").toLowerCase();
  return SUPPORTED_BRIEFING_LANGUAGES.has(code) ? code : "en";
}

function getGreeting(copy) {
  const hour = new Date().getHours();
  if (hour < 11) return copy.greetings.morning;
  if (hour < 18) return copy.greetings.day;
  return copy.greetings.evening;
}

function getGreetingHeadline(copy, languageCode, firstName = "") {
  const cleanFirstName = String(firstName || "").trim();
  if (languageCode === "de" && cleanFirstName) {
    return `${copy.greetings.day}, ${cleanFirstName}`;
  }

  const greeting = getGreeting(copy);
  return cleanFirstName ? `${greeting}, ${cleanFirstName}` : greeting;
}

function getSummary(copy, emphasis) {
  return copy.summary[emphasis] || copy.summary.steady;
}

function formatDate(value, languageCode) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString(LOCALES[languageCode] || LOCALES.en, {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function getLaneLabel(copy, lane) {
  if (lane === "obligation") return copy.labels.obligation;
  if (lane === "blocker") return copy.labels.blocker;
  return copy.labels.accelerator;
}

function getOpenLabel(copy, item) {
  if (item.variant === "no_obligation") return copy.actions.openBoard;
  if (item.open_kind === "case") return copy.actions.openCase;
  if (item.open_kind === "message") return copy.actions.openMessage;
  if (item.open_kind === "opportunity") return copy.actions.openOpportunity;
  return copy.actions.openArea;
}

function getNextLabel(copy, actionKind) {
  if (actionKind === "respond_case") return copy.actions.respondCase;
  if (actionKind === "complete_profile") return copy.actions.completeProfile;
  if (actionKind === "complete_cv") return copy.actions.completeCv;
  if (actionKind === "complete_skills") return copy.actions.completeSkills;
  if (actionKind === "start_language") return copy.actions.startLanguage;
  if (actionKind === "continue_language") return copy.actions.continueLanguage;
  if (actionKind === "review_release") return copy.actions.reviewRelease;
  if (actionKind === "open_matches") return copy.actions.openMatches;
  if (actionKind === "review_progress") return copy.actions.reviewProgress;
  if (actionKind === "review_account") return copy.actions.reviewAccount;
  return copy.actions.nextGeneric;
}

const LOCALIZED_TITLES = {
  no_obligation: {
    de: "Board aktuell ruhig",
    en: "Board currently calm",
    fr: "Tableau calme pour le moment",
    tr: "Pano su anda sakin",
    vi: "Bang hien dang on dinh",
    uk: "Doshka zaraz spokiyna",
    bks: "Tabla je trenutno mirna",
    ar: "اللوحة هادئة حالياً",
  },
  no_blocker: {
    de: "Keine akuten Sichtbarkeitsblocker",
    en: "No acute visibility blockers",
    fr: "Aucun blocage de visibilite aigu",
    tr: "Acil gorunurluk engeli yok",
    vi: "Khong co diem chan hien thi cap bach",
    uk: "Nemae hostrykh blokeriv vydymosti",
    bks: "Nema akutnih blokera vidljivosti",
    ar: "لا توجد عوائق ظهور حادة",
  },
  steady_progress: {
    de: "Stabiler Fortschritt",
    en: "Stable progress",
    fr: "Progression stable",
    tr: "Istikrarli ilerleme",
    vi: "Tien do on dinh",
    uk: "Stalyy progres",
    bks: "Stabilan napredak",
    ar: "تقدم مستقر",
  },
  top_opportunity: {
    de: "Passende Chance",
    en: "Relevant opportunity",
    fr: "Opportunite pertinente",
    tr: "Uygun firsat",
    vi: "Co hoi phu hop",
    uk: "Vidpovidna mozhlyvist",
    bks: "Relevantna prilika",
    ar: "فرصة مناسبة",
  },
  next_action: {
    de: "Nächster Schritt",
    en: "Next step",
    fr: "Prochaine etape",
    tr: "Sonraki adim",
    vi: "Buoc tiep theo",
    uk: "Nastupnyy krok",
    bks: "Sljedeci korak",
    ar: "الخطوة التالية",
  },
};

function getLocalizedTitle(item, languageCode) {
  if (item.variant === "no_obligation") return LOCALIZED_TITLES.no_obligation[languageCode] || LOCALIZED_TITLES.no_obligation.en;
  if (item.variant === "no_blocker") return LOCALIZED_TITLES.no_blocker[languageCode] || LOCALIZED_TITLES.no_blocker.en;
  if (item.variant === "steady_progress") return LOCALIZED_TITLES.steady_progress[languageCode] || LOCALIZED_TITLES.steady_progress.en;
  if (item.variant === "top_opportunity" && item.title === "Passende Chance") {
    return LOCALIZED_TITLES.top_opportunity[languageCode] || LOCALIZED_TITLES.top_opportunity.en;
  }
  if (item.variant === "next_action" && (item.title === "Nächster Schritt" || item.title === "Naechster Schritt")) {
    return LOCALIZED_TITLES.next_action[languageCode] || LOCALIZED_TITLES.next_action.en;
  }
  return item.title;
}

function buildEnglishBody(item, languageCode) {
  const dateLabel = formatDate(item.due_at, languageCode);

  if (item.variant === "open_requirement") {
    return {
      what: dateLabel
        ? `"${item.title}" is still open in your messaging board. Visible deadline: ${dateLabel}.`
        : `"${item.title}" is still open in your messaging board.`,
      why: item.due_state === "overdue"
        ? "This matters because an open requirement is already overdue."
        : item.due_state === "due_soon"
          ? "This matters because an open requirement is due soon."
          : "This matters because open requirements belong at the top of today's priorities.",
    };
  }
  if (item.variant === "open_deadline") {
    return {
      what: dateLabel
        ? `"${item.title}" is linked to a visible deadline: ${dateLabel}.`
        : `"${item.title}" currently carries time pressure or an active deadline.`,
      why: item.due_state === "overdue"
        ? "This matters because the point is already overdue."
        : "This matters because deadlines should be cleared first.",
    };
  }
  if (item.variant === "message_review") {
    return {
      what: `"${item.title}" is currently leading in your messaging board.`,
      why: "This matters because it shapes your next visible step.",
    };
  }
  if (item.variant === "no_obligation") {
    return {
      what: "There is no hard deadline or open requirement sitting at the top right now.",
      why: "This matters because you can put more attention on visibility and opportunities today.",
    };
  }
  if (item.variant === "visibility_release_missing") {
    return {
      what: `"${item.title}" is still open in your applicant status.`,
      why: "This matters because employers still cannot see your existing signal.",
    };
  }
  if (item.variant === "language_missing") {
    return {
      what: `"${item.title}" is still open in your applicant status.`,
      why: "This matters because you still need a language signal for more visibility.",
    };
  }
  if (item.variant === "profile_incomplete") {
    return {
      what: `"${item.title}" is still visible in your applicant status.`,
      why: "This matters because an incomplete profile can reduce your visibility.",
    };
  }
  if (item.variant === "cv_incomplete") {
    return {
      what: `"${item.title}" is still visible in your applicant status.`,
      why: "This matters because work experience and education make your profile easier to assess.",
    };
  }
  if (item.variant === "skills_incomplete") {
    return {
      what: `"${item.title}" is still visible in your applicant status.`,
      why: "This matters because missing skills weaken your fit and visibility.",
    };
  }
  if (item.variant === "no_blocker") {
    return {
      what: "No acute visibility blocker is currently sitting at the top.",
      why: "This matters because you can focus more on progress and opportunities today.",
    };
  }
  if (item.variant === "top_opportunity") {
    return {
      what: `"${item.title}" is currently your most important visible opportunity${item.employer_name ? ` with ${item.employer_name}` : ""}.`,
      why: "This matters because this opportunity currently connects most directly to your visible profile.",
    };
  }
  if (item.variant === "active_chances") {
    return {
      what: `"${item.title}" is currently visible as an accelerator.`,
      why: "This matters because active opportunities can move your next step forward directly.",
    };
  }
  if (item.variant === "visible_signal") {
    return {
      what: `"${item.title}" is already visible in your current status.`,
      why: "This matters because released signals strengthen how employers can assess you.",
    };
  }
  if (item.variant === "language_ready") {
    return {
      what: `"${item.title}" is already visible in your current status.`,
      why: "This matters because your language signal is already helping your progress.",
    };
  }
  if (item.variant === "skills_visible") {
    return {
      what: `"${item.title}" is already visible in your current status.`,
      why: "This matters because strong skills make your profile clearer.",
    };
  }
  if (item.variant === "next_action") {
    return {
      what: `"${item.title}" is currently your clearest working area.`,
      why: "This matters because the system sees the most direct progress there today.",
    };
  }
  return {
    what: `"${item.title}" is a visible progress point for today.`,
    why: "This matters because a stable, clean status keeps you ready for the next opportunity.",
  };
}

function buildGermanBody(item) {
  const dateLabel = formatDate(item.due_at, "de");

  if (item.variant === "open_requirement") {
    return {
      what: dateLabel
        ? `Im Messaging Board ist "${item.title}" noch offen. Sichtbare Frist: ${dateLabel}.`
        : `Im Messaging Board ist "${item.title}" noch offen.`,
      why: item.due_state === "overdue"
        ? "Das ist wichtig, weil eine offene Anforderung bereits überfällig ist."
        : item.due_state === "due_soon"
          ? "Das ist wichtig, weil eine offene Anforderung bald fällig wird."
          : "Das ist wichtig, weil offene Anforderungen heute Vorrang haben.",
    };
  }
  if (item.variant === "open_deadline") {
    return {
      what: dateLabel
        ? `Für "${item.title}" ist eine sichtbare Frist aktiv: ${dateLabel}.`
        : `Für "${item.title}" liegt ein offener Punkt mit Frist oder Zeitdruck vor.`,
      why: item.due_state === "overdue"
        ? "Das ist wichtig, weil dieser Punkt bereits überfällig ist."
        : "Das ist wichtig, weil Fristen zuerst geklärt werden sollten.",
    };
  }
  if (item.variant === "message_review") {
    return {
      what: `"${item.title}" liegt im Messaging Board vorne.`,
      why: "Das ist wichtig, weil daraus gerade dein nächster sichtbarer Schritt entsteht.",
    };
  }
  if (item.variant === "no_obligation") {
    return {
      what: "Obenauf liegt gerade keine harte Frist und keine offene Pflichtanforderung.",
      why: "Das ist wichtig, weil du deinen Fokus heute stärker auf Sichtbarkeit und Chancen legen kannst.",
    };
  }
  if (item.variant === "visibility_release_missing") {
    return {
      what: `"${item.title}" ist aktuell noch offen.`,
      why: "Das ist wichtig, weil Arbeitgeber dein vorhandenes Signal sonst noch nicht sehen.",
    };
  }
  if (item.variant === "language_missing") {
    return {
      what: `"${item.title}" ist aktuell noch offen.`,
      why: "Das ist wichtig, weil ohne Deutschsignal Vergleich und Sichtbarkeit fehlen.",
    };
  }
  if (item.variant === "profile_incomplete") {
    return {
      what: `"${item.title}" ist im Applicant-Stand noch sichtbar.`,
      why: "Das ist wichtig, weil ein unvollständiges Profil deine Sichtbarkeit bremst.",
    };
  }
  if (item.variant === "cv_incomplete") {
    return {
      what: `"${item.title}" ist aktuell noch offen.`,
      why: "Das ist wichtig, weil Erfahrung und Ausbildung dein Profil für Firmen anschlussfähig machen.",
    };
  }
  if (item.variant === "skills_incomplete") {
    return {
      what: `"${item.title}" ist aktuell noch offen.`,
      why: "Das ist wichtig, weil fehlende Skills deine Passung und Sichtbarkeit schwächen.",
    };
  }
  if (item.variant === "no_blocker") {
    return {
      what: "Gerade ist kein akuter Sichtbarkeitsblocker obenauf sichtbar.",
      why: "Das ist wichtig, weil du dich heute stärker auf Fortschritt und Chancen konzentrieren kannst.",
    };
  }
  if (item.variant === "top_opportunity") {
    return {
      what: `"${item.title}" ist aktuell deine wichtigste sichtbare Chance${item.employer_name ? ` bei ${item.employer_name}` : ""}.`,
      why: "Das ist wichtig, weil diese Chance derzeit am direktesten zu deinem sichtbaren Profil anschließt.",
    };
  }
  if (item.variant === "active_chances") {
    return {
      what: `"${item.title}" ist gerade als Beschleuniger sichtbar.`,
      why: "Das ist wichtig, weil laufende Chancen deinen nächsten Schritt direkt voranbringen können.",
    };
  }
  if (item.variant === "visible_signal") {
    return {
      what: `"${item.title}" ist in deinem sichtbaren Stand bereits da.`,
      why: "Das ist wichtig, weil freigegebene Signale deine Einordnung für Arbeitgeber stärken.",
    };
  }
  if (item.variant === "language_ready") {
    return {
      what: `"${item.title}" ist bereits sichtbar.`,
      why: "Das ist wichtig, weil dein Sprachsignal jetzt als Fortschrittstreiber wirkt.",
    };
  }
  if (item.variant === "skills_visible") {
    return {
      what: `"${item.title}" ist bereits sichtbar.`,
      why: "Das ist wichtig, weil gute Skills deine Passung klarer machen.",
    };
  }
  if (item.variant === "next_action") {
    return {
      what: `"${item.title}" ist aktuell dein klarer nächster Arbeitsbereich.`,
      why: "Das ist wichtig, weil das System hier heute den direktesten Fortschritt sieht.",
    };
  }
  return {
    what: `"${item.title}" ist heute ein sichtbarer Fortschrittspunkt.`,
    why: "Das ist wichtig, weil ein ruhiger, sauberer Stand dich für die nächste Chance bereit hält.",
  };
}

function buildFrenchBody(item) {
  const dateLabel = formatDate(item.due_at, "fr");

  if (item.variant === "open_requirement") {
    return {
      what: dateLabel
        ? `Dans ton tableau de messages, "${item.title}" est encore ouvert. Date limite visible : ${dateLabel}.`
        : `Dans ton tableau de messages, "${item.title}" est encore ouvert.`,
      why: item.due_state === "overdue"
        ? "C'est important parce qu'une demande ouverte est deja en retard."
        : item.due_state === "due_soon"
          ? "C'est important parce qu'une demande ouverte arrive bientot a echeance."
          : "C'est important parce que les demandes ouvertes doivent passer en priorite aujourd'hui.",
    };
  }
  if (item.variant === "open_deadline") {
    return {
      what: dateLabel
        ? `Pour "${item.title}", une echeance visible est active : ${dateLabel}.`
        : `Pour "${item.title}", il y a actuellement une echeance ou une pression de temps.`,
      why: item.due_state === "overdue"
        ? "C'est important parce que ce point est deja en retard."
        : "C'est important parce qu'il faut clarifier les echeances d'abord.",
    };
  }
  if (item.variant === "message_review") {
    return {
      what: `"${item.title}" passe actuellement en tete dans ton tableau de messages.`,
      why: "C'est important parce que cela definit ton prochain pas visible.",
    };
  }
  if (item.variant === "no_obligation") {
    return {
      what: "Aucune echeance dure ni demande obligatoire ouverte n'est en tete pour le moment.",
      why: "C'est important parce que tu peux te concentrer davantage sur la visibilite et les opportunites aujourd'hui.",
    };
  }
  if (item.variant === "visibility_release_missing" || item.variant === "language_missing" || item.variant === "cv_incomplete" || item.variant === "skills_incomplete") {
    return {
      what: `"${item.title}" est encore ouvert dans ton statut applicant.`,
      why: item.variant === "visibility_release_missing"
        ? "C'est important parce que les employeurs ne peuvent pas encore voir ton signal actuel."
        : item.variant === "language_missing"
          ? "C'est important parce qu'il te faut encore un signal de langue pour plus de visibilite."
          : item.variant === "cv_incomplete"
            ? "C'est important parce que l'experience et la formation rendent ton profil plus facile a evaluer."
            : "C'est important parce que des competences manquantes affaiblissent ton adequation et ta visibilite.",
    };
  }
  if (item.variant === "profile_incomplete") {
    return {
      what: `"${item.title}" reste visible dans ton statut applicant.`,
      why: "C'est important parce qu'un profil incomplet peut reduire ta visibilite.",
    };
  }
  if (item.variant === "no_blocker") {
    return {
      what: "Aucun blocage de visibilite aigu n'est actuellement en tete.",
      why: "C'est important parce que tu peux te concentrer davantage sur le progres et les opportunites aujourd'hui.",
    };
  }
  if (item.variant === "top_opportunity") {
    return {
      what: `"${item.title}" est actuellement ton opportunite visible la plus importante${item.employer_name ? ` chez ${item.employer_name}` : ""}.`,
      why: "C'est important parce que cette opportunite correspond le plus directement a ton profil visible.",
    };
  }
  if (item.variant === "active_chances") {
    return {
      what: `"${item.title}" est actuellement visible comme accelerateur.`,
      why: "C'est important parce que les opportunites actives peuvent faire avancer directement ta prochaine etape.",
    };
  }
  if (item.variant === "visible_signal" || item.variant === "language_ready" || item.variant === "skills_visible") {
    return {
      what: `"${item.title}" est deja visible dans ton statut actuel.`,
      why: item.variant === "visible_signal"
        ? "C'est important parce que les signaux diffuses renforcent la facon dont les employeurs peuvent t'evaluer."
        : item.variant === "language_ready"
          ? "C'est important parce que ton signal linguistique soutient deja ta progression."
          : "C'est important parce que de bonnes competences rendent ton profil plus clair.",
    };
  }
  if (item.variant === "next_action") {
    return {
      what: `"${item.title}" est actuellement ta zone de travail la plus claire.`,
      why: "C'est important parce que le systeme y voit aujourd'hui le progres le plus direct.",
    };
  }
  return {
    what: `"${item.title}" est un point de progression visible pour aujourd'hui.`,
    why: "C'est important parce qu'un statut stable et propre te garde pret pour la prochaine opportunite.",
  };
}

function buildTurkishBody(item) {
  const dateLabel = formatDate(item.due_at, "tr");

  if (item.variant === "open_requirement") {
    return {
      what: dateLabel
        ? `Mesaj panonda "${item.title}" hala acik. Gorunen son tarih: ${dateLabel}.`
        : `Mesaj panonda "${item.title}" hala acik.`,
      why: item.due_state === "overdue"
        ? "Bu onemli cunku acik bir talep zaten gecikmis durumda."
        : item.due_state === "due_soon"
          ? "Bu onemli cunku acik bir talep yakinda son tarihe ulasiyor."
          : "Bu onemli cunku acik talepler bugun onceligin ust siralarinda olmali.",
    };
  }
  if (item.variant === "open_deadline") {
    return {
      what: dateLabel
        ? `"${item.title}" icin gorunur bir son tarih aktif: ${dateLabel}.`
        : `"${item.title}" icin su anda zaman baskisi veya aktif bir son tarih var.`,
      why: item.due_state === "overdue"
        ? "Bu onemli cunku bu nokta zaten gecikmis durumda."
        : "Bu onemli cunku son tarihler once netlestirilmeli.",
    };
  }
  if (item.variant === "message_review") {
    return {
      what: `"${item.title}" su anda mesaj panonda en ustte yer aliyor.`,
      why: "Bu onemli cunku bir sonraki gorunur adimini bu belirliyor.",
    };
  }
  if (item.variant === "no_obligation") {
    return {
      what: "Su anda en ustte sert bir son tarih ya da acik zorunlu talep yok.",
      why: "Bu onemli cunku bugun gorunurluk ve firsatlara daha fazla odaklanabilirsin.",
    };
  }
  if (item.variant === "visibility_release_missing" || item.variant === "language_missing" || item.variant === "cv_incomplete" || item.variant === "skills_incomplete") {
    return {
      what: `"${item.title}" applicant durumunda hala acik.`,
      why: item.variant === "visibility_release_missing"
        ? "Bu onemli cunku isverenler mevcut sinyalini henuz goremez."
        : item.variant === "language_missing"
          ? "Bu onemli cunku daha fazla gorunurluk icin hala bir dil sinyaline ihtiyacin var."
          : item.variant === "cv_incomplete"
            ? "Bu onemli cunku is deneyimi ve egitim profilinin degerlendirilmesini kolaylastirir."
            : "Bu onemli cunku eksik beceriler uyumunu ve gorunurlugunu zayiflatir.",
    };
  }
  if (item.variant === "profile_incomplete") {
    return {
      what: `"${item.title}" applicant durumunda hala gorunuyor.`,
      why: "Bu onemli cunku eksik bir profil gorunurlugunu azaltabilir.",
    };
  }
  if (item.variant === "no_blocker") {
    return {
      what: "Su anda en ustte acil bir gorunurluk engeli yok.",
      why: "Bu onemli cunku bugun ilerleme ve firsatlara daha cok odaklanabilirsin.",
    };
  }
  if (item.variant === "top_opportunity") {
    return {
      what: `"${item.title}" su anda en onemli gorunur firsatin${item.employer_name ? `: ${item.employer_name}` : ""}.`,
      why: "Bu onemli cunku bu firsat su anda gorunur profiline en dogrudan baglaniyor.",
    };
  }
  if (item.variant === "active_chances") {
    return {
      what: `"${item.title}" su anda bir hizlandirici olarak gorunuyor.`,
      why: "Bu onemli cunku aktif firsatlar bir sonraki adimini dogrudan hizlandirabilir.",
    };
  }
  if (item.variant === "visible_signal" || item.variant === "language_ready" || item.variant === "skills_visible") {
    return {
      what: `"${item.title}" mevcut durumunda zaten gorunur.`,
      why: item.variant === "visible_signal"
        ? "Bu onemli cunku paylasilan sinyaller isverenlerin seni degerlendirmesini guclendirir."
        : item.variant === "language_ready"
          ? "Bu onemli cunku dil sinyalin ilerlemene zaten katkida bulunuyor."
          : "Bu onemli cunku guclu beceriler profilini daha net hale getirir.",
    };
  }
  if (item.variant === "next_action") {
    return {
      what: `"${item.title}" su anda en net calisma alanin.`,
      why: "Bu onemli cunku sistem bugun en dogrudan ilerlemeyi burada goruyor.",
    };
  }
  return {
    what: `"${item.title}" bugun gorunur bir ilerleme noktasi.`,
    why: "Bu onemli cunku duzenli ve temiz bir durum seni bir sonraki firsata hazir tutar.",
  };
}

function buildVietnameseBody(item) {
  const dateLabel = formatDate(item.due_at, "vi");

  if (item.variant === "open_requirement") {
    return {
      what: dateLabel
        ? `Tren bang tin nhan, "${item.title}" van dang mo. Han hien thi: ${dateLabel}.`
        : `Tren bang tin nhan, "${item.title}" van dang mo.`,
      why: item.due_state === "overdue"
        ? "Dieu nay quan trong vi mot yeu cau dang mo da qua han."
        : item.due_state === "due_soon"
          ? "Dieu nay quan trong vi mot yeu cau dang mo sap den han."
          : "Dieu nay quan trong vi cac yeu cau dang mo nen duoc uu tien hom nay.",
    };
  }
  if (item.variant === "open_deadline") {
    return {
      what: dateLabel
        ? `Voi "${item.title}", hien co mot han hien thi: ${dateLabel}.`
        : `Voi "${item.title}", hien dang co ap luc thoi gian hoac mot han xu ly.`,
      why: item.due_state === "overdue"
        ? "Dieu nay quan trong vi diem nay da qua han."
        : "Dieu nay quan trong vi han can duoc xu ly truoc.",
    };
  }
  if (item.variant === "message_review") {
    return {
      what: `"${item.title}" hien dang dung dau tren bang tin nhan cua ban.`,
      why: "Dieu nay quan trong vi no xac dinh buoc hien thi tiep theo cua ban.",
    };
  }
  if (item.variant === "no_obligation") {
    return {
      what: "Hien chua co han cung hay yeu cau bat buoc mo nao nam o tren cung.",
      why: "Dieu nay quan trong vi hom nay ban co the tap trung nhieu hon vao do hien thi va co hoi.",
    };
  }
  if (item.variant === "visibility_release_missing" || item.variant === "language_missing" || item.variant === "cv_incomplete" || item.variant === "skills_incomplete") {
    return {
      what: `"${item.title}" van dang mo trong trang thai applicant cua ban.`,
      why: item.variant === "visibility_release_missing"
        ? "Dieu nay quan trong vi nha tuyen dung van chua the thay tin hieu hien co cua ban."
        : item.variant === "language_missing"
          ? "Dieu nay quan trong vi ban van can mot tin hieu ngon ngu de tang do hien thi."
          : item.variant === "cv_incomplete"
            ? "Dieu nay quan trong vi kinh nghiem va hoc van giup ho so cua ban de duoc danh gia hon."
            : "Dieu nay quan trong vi ky nang thieu lam yeu do phu hop va do hien thi cua ban.",
    };
  }
  if (item.variant === "profile_incomplete") {
    return {
      what: `"${item.title}" van con hien thi trong trang thai applicant cua ban.`,
      why: "Dieu nay quan trong vi ho so chua day du co the lam giam do hien thi cua ban.",
    };
  }
  if (item.variant === "no_blocker") {
    return {
      what: "Hien khong co diem chan hien thi cap bach nam o tren cung.",
      why: "Dieu nay quan trong vi hom nay ban co the tap trung nhieu hon vao tien trien va co hoi.",
    };
  }
  if (item.variant === "top_opportunity") {
    return {
      what: `"${item.title}" hien la co hoi hien thi quan trong nhat cua ban${item.employer_name ? ` tai ${item.employer_name}` : ""}.`,
      why: "Dieu nay quan trong vi co hoi nay hien gan truc tiep nhat voi ho so dang hien thi cua ban.",
    };
  }
  if (item.variant === "active_chances") {
    return {
      what: `"${item.title}" hien dang hien thi nhu mot dong luc tang toc.`,
      why: "Dieu nay quan trong vi cac co hoi dang hoat dong co the day buoc tiep theo cua ban di len truc tiep.",
    };
  }
  if (item.variant === "visible_signal" || item.variant === "language_ready" || item.variant === "skills_visible") {
    return {
      what: `"${item.title}" da hien thi trong trang thai hien tai cua ban.`,
      why: item.variant === "visible_signal"
        ? "Dieu nay quan trong vi cac tin hieu da phat hanh giup nha tuyen dung danh gia ban ro hon."
        : item.variant === "language_ready"
          ? "Dieu nay quan trong vi tin hieu ngon ngu cua ban da ho tro tien trinh hien tai."
          : "Dieu nay quan trong vi ky nang manh lam ho so cua ban ro rang hon.",
    };
  }
  if (item.variant === "next_action") {
    return {
      what: `"${item.title}" hien la khu vuc lam viec ro nhat cua ban.`,
      why: "Dieu nay quan trong vi he thong nhin thay tien trien truc tiep nhat o day hom nay.",
    };
  }
  return {
    what: `"${item.title}" la mot diem tien trien hien thi cho hom nay.`,
    why: "Dieu nay quan trong vi trang thai on dinh va ro rang giu ban san sang cho co hoi tiep theo.",
  };
}

function buildUkrainianBody(item) {
  const dateLabel = formatDate(item.due_at, "uk");

  if (item.variant === "open_requirement") {
    return {
      what: dateLabel
        ? `U tvoyiy doshci povidomlen "${item.title}" dosi vidkryte. Vydymyy strok: ${dateLabel}.`
        : `U tvoyiy doshci povidomlen "${item.title}" dosi vidkryte.`,
      why: item.due_state === "overdue"
        ? "Tse vazhlyvo, bo vidkryta vymoha vzhe prostrochena."
        : item.due_state === "due_soon"
          ? "Tse vazhlyvo, bo vidkryta vymoha skoro stane strokovoyu."
          : "Tse vazhlyvo, bo vidkryti vymohy mayut buty sered priorytetiv sohodni.",
    };
  }
  if (item.variant === "open_deadline") {
    return {
      what: dateLabel
        ? `Dlya "${item.title}" zaraz die vydymyy strok: ${dateLabel}.`
        : `Dlya "${item.title}" zaraz ye chasovyy tysk abo aktyvnyy strok.`,
      why: item.due_state === "overdue"
        ? "Tse vazhlyvo, bo tsya tochka vzhe prostrochena."
        : "Tse vazhlyvo, bo stroky potribno ziasuvaty spershu.",
    };
  }
  if (item.variant === "message_review") {
    return {
      what: `"${item.title}" zaraz stoyit pershym u tvoyiy doshci povidomlen.`,
      why: "Tse vazhlyvo, bo same zvydsy formuyetsya tviy nastupnyy vydymyy krok.",
    };
  }
  if (item.variant === "no_obligation") {
    return {
      what: "Zaraz uhori nemae zhorskogo stroku abo vidkrytoyi oboviazkovoyi vymohy.",
      why: "Tse vazhlyvo, bo sohodni ty mozhesh bilshe zoseredytysya na vydymosti ta mozhlyvostyakh.",
    };
  }
  if (item.variant === "visibility_release_missing" || item.variant === "language_missing" || item.variant === "cv_incomplete" || item.variant === "skills_incomplete") {
    return {
      what: `"${item.title}" dosi vidkryte u tvoyemu applicant-statusi.`,
      why: item.variant === "visibility_release_missing"
        ? "Tse vazhlyvo, bo robotodavtsi shche ne mozhut bachyty tviy nayavnyy syhnal."
        : item.variant === "language_missing"
          ? "Tse vazhlyvo, bo tobi shche potriben movnyy syhnal dlya bilshoyi vydymosti."
          : item.variant === "cv_incomplete"
            ? "Tse vazhlyvo, bo dosvid i osvita robyat tviy profil legshym dlya otsinky."
            : "Tse vazhlyvo, bo vidsutni navychky poslablyuyut tvoyu vidpovidnist i vydymist.",
    };
  }
  if (item.variant === "profile_incomplete") {
    return {
      what: `"${item.title}" dosi vydymo u tvoyemu applicant-statusi.`,
      why: "Tse vazhlyvo, bo nepovernyy profil mozhe zmenshuvaty tvoyu vydymist.",
    };
  }
  if (item.variant === "no_blocker") {
    return {
      what: "Zaraz nemae hostroho blokera vydymosti u verkhniy chastyni.",
      why: "Tse vazhlyvo, bo sohodni ty mozhesh bilshe zoseredytysya na prohresi ta mozhlyvostyakh.",
    };
  }
  if (item.variant === "top_opportunity") {
    return {
      what: `"${item.title}" zaraz ye tvoyeyu nayvazhlyvishoyu vydymoyu mozhlyvistyu${item.employer_name ? ` u ${item.employer_name}` : ""}.`,
      why: "Tse vazhlyvo, bo tsya mozhlyvist zaraz naypryamishe poviazana z tvoyim vydymym profilem.",
    };
  }
  if (item.variant === "active_chances") {
    return {
      what: `"${item.title}" zaraz vydymo yak pryskoryuvach.`,
      why: "Tse vazhlyvo, bo aktyvni mozhlyvosti mozhut pryamo pryskoryty tviy nastupnyy krok.",
    };
  }
  if (item.variant === "visible_signal" || item.variant === "language_ready" || item.variant === "skills_visible") {
    return {
      what: `"${item.title}" uzhe vydymo u tvoyemu potochnomu statusi.`,
      why: item.variant === "visible_signal"
        ? "Tse vazhlyvo, bo vidkryti syhnaly pidsylyuyut te, yak robotodavtsi mozhut tebe otsinyty."
        : item.variant === "language_ready"
          ? "Tse vazhlyvo, bo tviy movnyy syhnal uzhe dopomahaye tvoyemu prohresu."
          : "Tse vazhlyvo, bo sylnі navychky robyat tviy profil zrozumilishym.",
    };
  }
  if (item.variant === "next_action") {
    return {
      what: `"${item.title}" zaraz ye tvoyeyu naychitkishoyu robochoyu zonoyu.`,
      why: "Tse vazhlyvo, bo systema bachyt tut naypryamishyy progres sohodni.",
    };
  }
  return {
    what: `"${item.title}" ye vydymoyu tochkoyu prohresu na sohodni.`,
    why: "Tse vazhlyvo, bo stabilnyy i chystyy status trymaye tebe hotovym do nastupnoyi mozhlyvosti.",
  };
}

function buildBksBody(item) {
  const dateLabel = formatDate(item.due_at, "bks");

  if (item.variant === "open_requirement") {
    return {
      what: dateLabel
        ? `Na tvojoj tabli poruka "${item.title}" je jos otvoreno. Vidljivi rok: ${dateLabel}.`
        : `Na tvojoj tabli poruka "${item.title}" je jos otvoreno.`,
      why: item.due_state === "overdue"
        ? "Ovo je vazno jer je otvoreni zahtjev vec zakasnio."
        : item.due_state === "due_soon"
          ? "Ovo je vazno jer otvoreni zahtjev uskoro dolazi na rok."
          : "Ovo je vazno jer otvoreni zahtjevi danas trebaju biti medju prvim prioritetima.",
    };
  }
  if (item.variant === "open_deadline") {
    return {
      what: dateLabel
        ? `Za "${item.title}" je aktivan vidljivi rok: ${dateLabel}.`
        : `Za "${item.title}" trenutno postoji vremenski pritisak ili aktivan rok.`,
      why: item.due_state === "overdue"
        ? "Ovo je vazno jer je ova tacka vec zakasnila."
        : "Ovo je vazno jer rokove treba prvo razjasniti.",
    };
  }
  if (item.variant === "message_review") {
    return {
      what: `"${item.title}" je trenutno na vrhu tvoje table poruka.`,
      why: "Ovo je vazno jer iz toga proizlazi tvoj sljedeci vidljivi korak.",
    };
  }
  if (item.variant === "no_obligation") {
    return {
      what: "Trenutno na vrhu nema tvrdog roka ni otvorenog obaveznog zahtjeva.",
      why: "Ovo je vazno jer se danas mozes vise fokusirati na vidljivost i prilike.",
    };
  }
  if (item.variant === "visibility_release_missing" || item.variant === "language_missing" || item.variant === "cv_incomplete" || item.variant === "skills_incomplete") {
    return {
      what: `"${item.title}" je jos otvoreno u tvom applicant statusu.`,
      why: item.variant === "visibility_release_missing"
        ? "Ovo je vazno jer poslodavci jos ne mogu vidjeti tvoj postojeci signal."
        : item.variant === "language_missing"
          ? "Ovo je vazno jer ti jos treba jezicki signal za vecu vidljivost."
          : item.variant === "cv_incomplete"
            ? "Ovo je vazno jer iskustvo i obrazovanje cine tvoj profil laksim za procjenu."
            : "Ovo je vazno jer nedostajuce vjestine slabe tvoju uskladjenost i vidljivost.",
    };
  }
  if (item.variant === "profile_incomplete") {
    return {
      what: `"${item.title}" je jos vidljivo u tvom applicant statusu.`,
      why: "Ovo je vazno jer nepotpun profil moze smanjiti tvoju vidljivost.",
    };
  }
  if (item.variant === "no_blocker") {
    return {
      what: "Trenutno na vrhu nema akutnog blokera vidljivosti.",
      why: "Ovo je vazno jer se danas mozes vise fokusirati na napredak i prilike.",
    };
  }
  if (item.variant === "top_opportunity") {
    return {
      what: `"${item.title}" je trenutno tvoja najvaznija vidljiva prilika${item.employer_name ? ` kod ${item.employer_name}` : ""}.`,
      why: "Ovo je vazno jer se ova prilika trenutno najdirektnije veze za tvoj vidljivi profil.",
    };
  }
  if (item.variant === "active_chances") {
    return {
      what: `"${item.title}" je trenutno vidljivo kao ubrzivac.`,
      why: "Ovo je vazno jer aktivne prilike mogu direktno ubrzati tvoj sljedeci korak.",
    };
  }
  if (item.variant === "visible_signal" || item.variant === "language_ready" || item.variant === "skills_visible") {
    return {
      what: `"${item.title}" je vec vidljivo u tvom trenutnom statusu.`,
      why: item.variant === "visible_signal"
        ? "Ovo je vazno jer objavljeni signali jacaju kako te poslodavci mogu procijeniti."
        : item.variant === "language_ready"
          ? "Ovo je vazno jer tvoj jezicki signal vec pomaze tvom napretku."
          : "Ovo je vazno jer jake vjestine cine tvoj profil jasnijim.",
    };
  }
  if (item.variant === "next_action") {
    return {
      what: `"${item.title}" je trenutno tvoja najjasnija radna zona.`,
      why: "Ovo je vazno jer sistem danas tu vidi najdirektniji napredak.",
    };
  }
  return {
    what: `"${item.title}" je danas vidljiva tacka napretka.`,
    why: "Ovo je vazno jer te stabilan i uredan status drzi spremnim za narednu priliku.",
  };
}

function buildArabicBody(item) {
  const dateLabel = formatDate(item.due_at, "ar");

  if (item.variant === "open_requirement") {
    return {
      what: dateLabel
        ? `في لوحة الرسائل لديك، ما زال "${item.title}" مفتوحاً. الموعد الظاهر: ${dateLabel}.`
        : `في لوحة الرسائل لديك، ما زال "${item.title}" مفتوحاً.`,
      why: item.due_state === "overdue"
        ? "هذا مهم لأن هناك طلباً مفتوحاً متأخراً بالفعل."
        : item.due_state === "due_soon"
          ? "هذا مهم لأن هناك طلباً مفتوحاً يقترب موعده النهائي."
          : "هذا مهم لأن الطلبات المفتوحة يجب أن تكون في أعلى أولويات اليوم.",
    };
  }
  if (item.variant === "open_deadline") {
    return {
      what: dateLabel
        ? `هناك موعد ظاهر مرتبط بـ "${item.title}": ${dateLabel}.`
        : `هناك ضغط زمني أو موعد نشط مرتبط بـ "${item.title}".`,
      why: item.due_state === "overdue"
        ? "هذا مهم لأن هذه النقطة متأخرة بالفعل."
        : "هذا مهم لأن المواعيد يجب أن تُحسم أولاً.",
    };
  }
  if (item.variant === "message_review") {
    return {
      what: `"${item.title}" يتصدر حالياً لوحة الرسائل لديك.`,
      why: "هذا مهم لأنه يحدد خطوتك الظاهرة التالية.",
    };
  }
  if (item.variant === "no_obligation") {
    return {
      what: "لا يوجد الآن موعد صارم أو طلب إلزامي مفتوح في المقدمة.",
      why: "هذا مهم لأنك تستطيع اليوم التركيز أكثر على الظهور والفرص.",
    };
  }
  if (item.variant === "visibility_release_missing" || item.variant === "language_missing" || item.variant === "cv_incomplete" || item.variant === "skills_incomplete") {
    return {
      what: `ما زال "${item.title}" مفتوحاً في حالة Applicant الخاصة بك.`,
      why: item.variant === "visibility_release_missing"
        ? "هذا مهم لأن أصحاب العمل لا يستطيعون بعد رؤية الإشارة الحالية لديك."
        : item.variant === "language_missing"
          ? "هذا مهم لأنك ما زلت بحاجة إلى إشارة لغوية لزيادة الظهور."
          : item.variant === "cv_incomplete"
            ? "هذا مهم لأن الخبرة والتعليم يسهلان تقييم ملفك."
            : "هذا مهم لأن المهارات الناقصة تضعف الملاءمة والظهور.",
    };
  }
  if (item.variant === "profile_incomplete") {
    return {
      what: `ما زال "${item.title}" ظاهراً في حالة Applicant الخاصة بك.`,
      why: "هذا مهم لأن الملف غير المكتمل قد يقلل من ظهورك.",
    };
  }
  if (item.variant === "no_blocker") {
    return {
      what: "لا يوجد حالياً عائق ظهور حاد في المقدمة.",
      why: "هذا مهم لأنك تستطيع اليوم التركيز أكثر على التقدم والفرص.",
    };
  }
  if (item.variant === "top_opportunity") {
    return {
      what: `"${item.title}" هي حالياً أهم فرصة ظاهرة لديك${item.employer_name ? ` لدى ${item.employer_name}` : ""}.`,
      why: "هذا مهم لأن هذه الفرصة ترتبط حالياً بشكل مباشر بملفك الظاهر.",
    };
  }
  if (item.variant === "active_chances") {
    return {
      what: `"${item.title}" تظهر حالياً كعامل تسريع.`,
      why: "هذا مهم لأن الفرص النشطة يمكن أن تدفع خطوتك التالية مباشرة.",
    };
  }
  if (item.variant === "visible_signal" || item.variant === "language_ready" || item.variant === "skills_visible") {
    return {
      what: `"${item.title}" ظاهر بالفعل في حالتك الحالية.`,
      why: item.variant === "visible_signal"
        ? "هذا مهم لأن الإشارات المفعلة تقوي طريقة تقييم أصحاب العمل لك."
        : item.variant === "language_ready"
          ? "هذا مهم لأن إشارتك اللغوية تساعد تقدمك بالفعل."
          : "هذا مهم لأن المهارات القوية تجعل ملفك أوضح.",
    };
  }
  if (item.variant === "next_action") {
    return {
      what: `"${item.title}" هي حالياً أوضح مساحة عمل لديك.`,
      why: "هذا مهم لأن النظام يرى هنا أكثر تقدم مباشر اليوم.",
    };
  }
  return {
    what: `"${item.title}" هي نقطة تقدم ظاهرة لليوم.`,
    why: "هذا مهم لأن الحالة المستقرة والواضحة تبقيك جاهزاً للفرصة التالية.",
  };
}

function localizeItem(copy, languageCode, item) {
  let body = buildEnglishBody(item, languageCode);
  if (languageCode === "de") body = buildGermanBody(item);
  else if (languageCode === "fr") body = buildFrenchBody(item);
  else if (languageCode === "tr") body = buildTurkishBody(item);
  else if (languageCode === "vi") body = buildVietnameseBody(item);
  else if (languageCode === "uk") body = buildUkrainianBody(item);
  else if (languageCode === "bks") body = buildBksBody(item);
  else if (languageCode === "ar") body = buildArabicBody(item);

  return {
    ...item,
    title: getLocalizedTitle(item, languageCode),
    laneLabel: getLaneLabel(copy, item.lane),
    what: body.what,
    why: body.why,
    coachWhy: buildGermanBody(item).why,
    openLabel: getOpenLabel(copy, item),
    nextLabel: getNextLabel(copy, item.next_action_kind),
  };
}

function buildCoachSurfaceOverride(item, items, copy) {
  const visibleMessages = items.map((entry) => ({
    key: entry.key,
    title: entry.title,
    text: entry.coachWhy || entry.why,
    href: entry.next_href || entry.href,
    sender_name: copy.coachLabel,
    sender_role: entry.laneLabel,
    next_step_label: entry.nextLabel,
    due_label: entry.due_at ? formatDate(entry.due_at, "de") : "",
    due_relative: entry.due_state === "overdue" ? "überfällig" : entry.due_state === "due_soon" ? "bald fällig" : "",
    status_labels: [entry.laneLabel],
  }));

  return {
    route_path: "/dashboard/applicant",
    screen_key: "applicant_dashboard",
    screen_label: "Applicant Dashboard",
    highlighted_step: `${item.title}: ${item.coachWhy || item.why}`,
    surface_state_key: `briefing:${item.key}`,
    surface_summary: {
      surface_type: "applicant_dashboard",
      prioritized_message: visibleMessages.find((entry) => entry.key === item.key) || visibleMessages[0] || null,
      visible_messages: visibleMessages,
      coach_next_step: {
        key: item.key,
        label_de: item.title,
        label_en: "briefing point",
        reason_de: item.coachWhy || item.why,
        href: item.next_href || item.href,
      },
      coach_missing_items: visibleMessages.map((entry) => ({
        key: entry.key,
        label_de: entry.title,
        label_en: "briefing point",
        reason_de: entry.text,
        href: entry.href,
      })),
    },
  };
}

function getStorageKeys(userid) {
  const dayKey = getApplicantBriefingDayKey();
  return {
    laterKey: `applicant-briefing:later:${userid}:${dayKey}`,
    hiddenKey: `applicant-briefing:hidden:${userid}:${dayKey}`,
  };
}

function getLocalStorageSafe() {
  if (typeof window === "undefined") return null;
  return window.localStorage;
}

function getSessionStorageSafe() {
  if (typeof window === "undefined") return null;
  return window.sessionStorage;
}

function readFlag(storage, key) {
  try {
    return storage?.getItem(key) === "1";
  } catch (_error) {
    return false;
  }
}

function writeFlag(storage, key, value) {
  try {
    if (!storage) return;
    if (value) storage.setItem(key, "1");
    else storage.removeItem(key);
  } catch (_error) {
    // Storage failures should not break the dashboard.
  }
}

function getStoredUserId() {
  try {
    return getLocalStorageSafe()?.getItem("userid") || "applicant";
  } catch (_error) {
    return "applicant";
  }
}

function getStoredFirstName() {
  try {
    return getLocalStorageSafe()?.getItem("firstname") || "";
  } catch (_error) {
    return "";
  }
}

function extractFirstName(displayName = "") {
  const cleaned = String(displayName || "").trim();
  if (!cleaned) return "";
  const token = cleaned.split(/\s+/)[0] || "";
  const normalized = token.toLowerCase();
  if (["bewerber", "applicant", "candidate", "profil", "profile", "user"].includes(normalized)) {
    return "";
  }
  return token;
}

export default function ApplicantDailyBriefing({
  briefing,
  loading = false,
  error = "",
  onNavigate,
  applicantFirstName = "",
  applicantDisplayName = "",
  forceCompact = false,
}) {
  const { askApplicantCoach, setCoachOpen, replyLanguage } = useApplicantCoach();
  const userid = getStoredUserId();
  const localStorageRef = getLocalStorageSafe();
  const sessionStorageRef = getSessionStorageSafe();
  const { laterKey, hiddenKey } = useMemo(() => getStorageKeys(userid), [userid]);
  const [deferred, setDeferred] = useState(() => readFlag(sessionStorageRef, laterKey));
  const [hiddenToday, setHiddenToday] = useState(() => readFlag(localStorageRef, hiddenKey));
  const languageCode = getBriefingLanguageCode(replyLanguage);
  const copy = useMemo(() => getCopy(languageCode), [languageCode]);
  const firstName = useMemo(() => {
    const preferredName = String(applicantFirstName || "").trim();
    if (preferredName) return preferredName;
    const storedFirstName = getStoredFirstName();
    if (storedFirstName) return storedFirstName;
    return extractFirstName(applicantDisplayName);
  }, [applicantDisplayName, applicantFirstName]);
  const items = useMemo(
    () => (briefing?.items || []).map((item) => localizeItem(copy, languageCode, item)),
    [briefing?.items, copy, languageCode]
  );
  const compactMode = forceCompact || deferred;

  useEffect(() => {
    setDeferred(readFlag(sessionStorageRef, laterKey));
    setHiddenToday(readFlag(localStorageRef, hiddenKey));
  }, [hiddenKey, laterKey, localStorageRef, sessionStorageRef]);

  const handleNavigate = useCallback((href) => {
    if (!href || !onNavigate) return;
    onNavigate(href);
  }, [onNavigate]);

  const handleExplain = useCallback(async (item) => {
    const surfaceContext = buildCoachSurfaceOverride(item, items, copy);
    await askApplicantCoach({
      question: item.why,
      quickAction: "message_priority",
      surfaceContext,
    });
    setCoachOpen(true);
  }, [askApplicantCoach, copy, items, setCoachOpen]);

  const handleLater = useCallback(() => {
    writeFlag(sessionStorageRef, laterKey, true);
    setDeferred(true);
  }, [laterKey, sessionStorageRef]);

  const handleShowNow = useCallback(() => {
    writeFlag(sessionStorageRef, laterKey, false);
    setDeferred(false);
  }, [laterKey, sessionStorageRef]);

  const handleHideToday = useCallback(() => {
    writeFlag(localStorageRef, hiddenKey, true);
    setHiddenToday(true);
  }, [hiddenKey, localStorageRef]);

  if (hiddenToday) {
    return null;
  }

  if (compactMode && loading && !briefing?.source_ready) {
    return (
      <section className="applicant-daily-briefing applicant-daily-briefing--compact" dir={replyLanguage?.direction || "ltr"}>
        <div className="applicant-daily-briefing__compact-main">
          <ApplicantCoachAvatar size={58} />
          <div className="applicant-daily-briefing__compact-copy">
            <span className="applicant-section-kicker">{copy.labels.compact}</span>
            <strong>{copy.loadingTitle}</strong>
            <p>{copy.loadingText}</p>
          </div>
        </div>
      </section>
    );
  }

  if (loading && !briefing?.source_ready) {
    return (
      <section className="applicant-daily-briefing" dir={replyLanguage?.direction || "ltr"}>
        <div className="applicant-daily-briefing__hero">
          <div className="applicant-daily-briefing__identity">
            <ApplicantCoachAvatar size={84} />
            <div className="applicant-daily-briefing__copy">
              <span className="applicant-section-kicker">{copy.coachLabel}</span>
              <h2>{copy.loadingTitle}</h2>
              <p>{copy.loadingText}</p>
            </div>
          </div>
        </div>
        <div className="applicant-daily-briefing__loading-grid" aria-hidden="true">
          {[1, 2, 3].map((item) => <div key={item} className="applicant-daily-briefing__skeleton" />)}
        </div>
      </section>
    );
  }

  if (compactMode && !items.length) {
    return (
      <section className="applicant-daily-briefing applicant-daily-briefing--compact" dir={replyLanguage?.direction || "ltr"}>
        <div className="applicant-daily-briefing__compact-main">
          <ApplicantCoachAvatar size={58} />
          <div className="applicant-daily-briefing__compact-copy">
            <span className="applicant-section-kicker">{copy.labels.compact}</span>
            <strong>{error ? copy.errorTitle : copy.emptyTitle}</strong>
            <p>{error ? copy.errorText : copy.emptyText}</p>
          </div>
        </div>
        <div className="applicant-daily-briefing__compact-actions">
          <button type="button" className="applicant-daily-briefing__ghost" onClick={() => handleNavigate("/dashboard/applicant/nachrichten")}>
            {copy.actions.openBoard}
          </button>
        </div>
      </section>
    );
  }

  if (!items.length) {
    return (
      <section className="applicant-daily-briefing" dir={replyLanguage?.direction || "ltr"}>
        <div className="applicant-daily-briefing__hero">
          <div className="applicant-daily-briefing__identity">
            <ApplicantCoachAvatar size={84} />
            <div className="applicant-daily-briefing__copy">
              <span className="applicant-section-kicker">{copy.coachLabel}</span>
              <h2>{error ? copy.errorTitle : copy.emptyTitle}</h2>
              <p>{error ? copy.errorText : copy.emptyText}</p>
            </div>
          </div>
          <div className="applicant-daily-briefing__hero-actions">
            <button type="button" className="applicant-daily-briefing__ghost" onClick={() => handleNavigate("/dashboard/applicant/nachrichten")}>
              {copy.actions.openBoard}
            </button>
            <button type="button" className="applicant-daily-briefing__primary" onClick={() => handleNavigate("/dashboard/applicant")}>
              {copy.actions.nextGeneric}
            </button>
          </div>
        </div>
      </section>
    );
  }

  if (compactMode) {
    return (
      <section className="applicant-daily-briefing applicant-daily-briefing--compact" dir={replyLanguage?.direction || "ltr"}>
        <div className="applicant-daily-briefing__compact-main">
          <ApplicantCoachAvatar size={58} />
          <div className="applicant-daily-briefing__compact-copy">
            <span className="applicant-section-kicker">{copy.labels.compact}</span>
            <strong>{getGreetingHeadline(copy, languageCode, firstName)} · {copy.coachLabel}</strong>
            <p>{getSummary(copy, briefing?.emphasis)}</p>
          </div>
        </div>
        <div className="applicant-daily-briefing__compact-actions">
          <button type="button" className="applicant-daily-briefing__ghost" onClick={handleShowNow}>
            {copy.actions.now}
          </button>
          <button type="button" className="applicant-daily-briefing__primary" onClick={() => handleExplain(items[0])}>
            {copy.actions.explain}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="applicant-daily-briefing" dir={replyLanguage?.direction || "ltr"}>
      <div className="applicant-daily-briefing__hero">
        <div className="applicant-daily-briefing__identity">
          <ApplicantCoachAvatar size={92} />
          <div className="applicant-daily-briefing__copy">
            <span className="applicant-section-kicker">{copy.coachLabel}</span>
            <h2>{getGreetingHeadline(copy, languageCode, firstName)}</h2>
            <p>{getSummary(copy, briefing?.emphasis)}</p>
          </div>
        </div>

        <div className="applicant-daily-briefing__hero-actions">
          <button type="button" className="applicant-daily-briefing__ghost" onClick={() => handleExplain(items[0])}>
            <Bot size={15} />
            <span>{copy.actions.explain}</span>
          </button>
          <button type="button" className="applicant-daily-briefing__ghost" onClick={handleLater}>
            {copy.actions.later}
          </button>
          <button type="button" className="applicant-daily-briefing__ghost" onClick={handleHideToday}>
            {copy.actions.hideToday}
          </button>
        </div>
      </div>

      <div className="applicant-daily-briefing__grid">
        {items.slice(0, 3).map((item) => {
          const LaneIcon = LANE_ICONS[item.lane] || Sparkles;
          return (
            <article key={item.key} className={`applicant-daily-briefing__card applicant-daily-briefing__card--${item.lane}`}>
              <div className="applicant-daily-briefing__card-head">
                <div className="applicant-daily-briefing__rank">
                  <span>{item.priority_rank}</span>
                </div>
                <div className="applicant-daily-briefing__card-copy">
                  <div className="applicant-daily-briefing__lane">
                    <LaneIcon size={15} />
                    <span>{item.laneLabel}</span>
                  </div>
                  <h3>{item.title}</h3>
                </div>
              </div>

              <div className="applicant-daily-briefing__body">
                <div className="applicant-daily-briefing__detail">
                  <span>{copy.labels.what}</span>
                  <p>{item.what}</p>
                </div>
                <div className="applicant-daily-briefing__detail">
                  <span>{copy.labels.why}</span>
                  <p>{item.why}</p>
                </div>
                <div className="applicant-daily-briefing__detail">
                  <span>{copy.labels.next}</span>
                  <p>{item.nextLabel}</p>
                </div>
              </div>

              <div className="applicant-daily-briefing__actions">
                <button type="button" className="applicant-daily-briefing__subtle" onClick={() => handleNavigate(item.href)}>
                  <span>{item.openLabel}</span>
                </button>
                <button type="button" className="applicant-daily-briefing__subtle" onClick={() => handleExplain(item)}>
                  <span>{copy.actions.explain}</span>
                </button>
                <button type="button" className="applicant-daily-briefing__primary" onClick={() => handleNavigate(item.next_href || item.href)}>
                  <span>{item.nextLabel}</span>
                  <ArrowRight size={15} />
                </button>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
